/**
 * RecentItemsService
 *
 * @author Copyright (c) 2021 Salesforce.com.
 * @ownership Appexchange Intelligence
 */
import { CookieConsentCategory, createCatCookie, getCookieConsented } from '../utilities/cookies';

const RECENT_ITEMS = 'appx_recent_items';
const RECENT_ITEMS_LEGACY = 'apex__appxSearch';
const RECENT_ITEMS_SIZE = 5;

export interface RecentItem {
    listingId: string;
    timestamp: number;
}
export interface RecentItemsResult {
    recentItems: RecentItem[];
}

export class RecentItemService {
    async getRecentItems(_: string): Promise<RecentItemsResult> {
        const recentItemsFromLegacy = RecentItemService.getRecentItemsLegacy();
        const response = this.getCookie(RECENT_ITEMS);
        const recentItemsFromCookie: RecentItem[] = JSON.parse(response);
        return Promise.resolve({ recentItems: [...recentItemsFromCookie, ...recentItemsFromLegacy] });
    }

    async updateRecentItems(recentItem: RecentItem): Promise<RecentItem[]> {
        const recentItemElements: RecentItem[] = JSON.parse(this.getCookie(RECENT_ITEMS)) || [];
        const recentItemsWithoutListing = recentItemElements.filter((item) => item.listingId !== recentItem.listingId);

        const recentItemsUpdated = [...recentItemsWithoutListing, recentItem]
            .sort(RecentItemService.byTimestampDesc)
            .slice(0, RECENT_ITEMS_SIZE);
        createCatCookie(RECENT_ITEMS, JSON.stringify(recentItemsUpdated), CookieConsentCategory.Functional);
        return recentItemsUpdated;
    }

    // @ts-expect-error * Automated Type Error Fix
    private static byTimestampDesc(recentItem1, recentItem2) {
        return recentItem2.timestamp - recentItem1.timestamp;
    }

    private static getRecentItemsLegacy(): RecentItem[] {
        const response = getCookieConsented(RECENT_ITEMS_LEGACY, CookieConsentCategory.Functional);
        return RecentItemService.parseCookie(response);
    }

    private static parseCookie(response: string): RecentItem[] {
        if (response) {
            const decodedResponse = unescape(decodeURI(response));
            return decodedResponse.split(';').map(RecentItemService.parseRecentItem);
        }
        return [];
    }

    // @ts-expect-error * Automated Type Error Fix
    private static parseRecentItem(recentItemFromCookie): RecentItem {
        const recentItemAsList = recentItemFromCookie.split('_');
        return {
            listingId: recentItemAsList[0],
            timestamp: recentItemFromCookie[1],
        };
    }

    private getCookie(name: string): string {
        return getCookieConsented(name, CookieConsentCategory.Functional) || '[]';
    }
}
